import React, { useEffect, useMemo, useState, useCallback } from "react";
import CreateAchatModal from "./createAchatModal";
import EditAchatModal from "./EditAchatModal";
import CreateSpecialAchatModal from "./CreateSpecialAchatModal";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import Button from "../../../components/Button/Button";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import {
  getReglements,
  deleteReglement,
  updateReglement,
} from "../../../services/achatsServices";
import { getSettings } from "../../../services/settingsServices";
import { getArticles } from "../../../services/articlesServices";
import { getAllUsers } from "../../../services/userServices";
import { sendReminderMail } from "../../../services/mailServices";
import { FaCartPlus, FaInfoCircle } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import FiltersToolbar from "./components/FiltersToolbar";
import TotalsBar from "./components/TotalsBar";
import DesktopTable from "./components/DesktopTable";
import MobileCards from "./components/MobileCards";
import MessageModal from "./components/MessageModal";
import ReminderHistoryModal from "./components/ReminderHistoryModal";

import {
  norm,
  matchAllTokens,
  statusChip,
  paymentPlanTag,
  getUniqueSeasons,
  isWithinSeason,
} from "./helpers";

const Achats = () => {
  useScrollToTop();

  // User / role
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Data
  const [reglements, setReglements] = useState([]);
  const [articles, setArticles] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [adherentsByUser, setAdherentsByUser] = useState({}); // userId -> [adhérents]

  // UI state
  const [isModalActive, setIsModalActive] = useState(false);
  const [isSpecialModalActive, setIsSpecialModalActive] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableReglement, setEditableReglement] = useState(null);

  // Filters
  const [filterNom, setFilterNom] = useState("");
  const [filterArticle, setFilterArticle] = useState("");
  const [filterStatutReglementList, setFilterStatutReglementList] = useState(
    [],
  ); // multi-statuts
  const [filterTypeReglement, setFilterTypeReglement] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");

  // Message viewer (side modal)
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageReglement, setMessageReglement] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // Messages de rappel
  const [reminderHistoryOpen, setReminderHistoryOpen] = useState(false);
  const [reminderHistoryReglement, setReminderHistoryReglement] =
    useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [reglementsData, settingsData, articlesData, usersData] =
        await Promise.all([
          getReglements(),
          getSettings(),
          getArticles(),
          getAllUsers(),
        ]);

      setReglements(reglementsData);
      setArticles(articlesData);

      // Map userId -> adhérents
      const byUser = {};
      (usersData || []).forEach((u) => {
        byUser[u.id] = u.adherents || [];
      });
      setAdherentsByUser(byUser);

      // Saison active depuis settings
      const active = settingsData?.[0]?.saison_active;
      setActiveSeason(active || "");

      // Saisons disponibles
      const unique = getUniqueSeasons(reglementsData);
      if (active && !unique.includes(active)) unique.push(active);
      unique.sort();
      setSeasons(unique);
      setSelectedSeason(active || "");
    } catch (e) {
      console.error("Erreur lors du chargement des données:", e);
      toast.error("Erreur lors du chargement des données.");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openReminderHistory = (reglement) => {
    setReminderHistoryReglement(reglement);
    setReminderHistoryOpen(true);
  };

  const closeReminderHistory = () => {
    setReminderHistoryOpen(false);
    setReminderHistoryReglement(null);
  };

  // Filtrage
  const filteredReglements = useMemo(() => {
    const q = filterNom.trim();
    return reglements.filter((r) => {
      // match nom/prénom user + adhérents rattachés (multi mots, sans accents)
      const userFull = `${r.User?.prenom || ""} ${r.User?.nom || ""}`.trim();
      const adhs = adherentsByUser[r.User?.id] || [];
      const adhStr = adhs
        .map((a) => `${a.prenom || ""} ${a.nom || ""}`.trim())
        .join(" ");
      const email = r.User?.email || "";

      const matchesNom =
        !q || matchAllTokens(`${userFull} ${adhStr} ${email}`, q);

      const matchesArticle = !filterArticle
        ? true
        : r.Article?.nom === filterArticle ||
          (filterArticle === "Règlement Inscription" && !r.Article);

      const matchesStatut =
        filterStatutReglementList.length === 0 ||
        filterStatutReglementList.includes(r.statut_reglement);

      const matchesType = !filterTypeReglement
        ? true
        : r.type_reglement === filterTypeReglement;

      const matchesSeason =
        !selectedSeason || isWithinSeason(r.date_achat, selectedSeason);

      return (
        matchesNom &&
        matchesArticle &&
        matchesStatut &&
        matchesType &&
        matchesSeason
      );
    });
  }, [
    reglements,
    filterNom,
    filterArticle,
    filterStatutReglementList,
    filterTypeReglement,
    selectedSeason,
    adherentsByUser,
  ]);

  // Totaux
  const totalAchats = useMemo(
    () =>
      filteredReglements
        .reduce((acc, cur) => acc + parseFloat(cur.prix_total || 0), 0)
        .toFixed(2),
    [filteredReglements],
  );
  const totalAchatsCount = filteredReglements.length;

  // CRUD + actions
  const refreshAchats = async () => {
    try {
      const data = await getReglements();
      setReglements(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAchat = async (id) => {
    if (!window.confirm("Supprimer cet achat ?")) return;
    try {
      await deleteReglement(id);
      toast.success("Achat supprimé avec succès.");
      refreshAchats();
      if (messageOpen) {
        setMessageOpen(false);
        setMessageReglement(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression de l'achat.");
    }
  };

  const openEditModal = (reglement) => {
    setEditableReglement(reglement);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const handleSendReminderMail = async (reglement) => {
    const email = reglement.User?.email;

    if (!email) {
      toast.error("Aucune adresse email trouvée pour cet utilisateur.");
      return;
    }

    const userNom = `${reglement.User?.prenom || ""} ${
      reglement.User?.nom || ""
    }`.trim();

    const article = reglement.Article?.nom || "Règlement Inscription";
    const prix = parseFloat(reglement.prix_total || 0).toFixed(2);
    const date = new Date(reglement.date_achat).toLocaleDateString("fr-FR");

    const subject = "🔔 Rappel de paiement en attente";

    const text = `Bonjour ${userNom},

Nous vous rappelons que le paiement suivant est en attente :

- Article : ${article}
- Montant : ${prix} €
- Date d'achat : ${date}

Merci de procéder au règlement ou de contacter le club pour toute question.
https://www.judo-taiso-saint-loubes.com/commandes-&-reglements

Sportivement,
Le club`;

    try {
      const response = await sendReminderMail({
        achat_id: reglement.id,
        email,
        subject,
        text,
      });

      toast.success(
        `Mail de rappel envoyé ! Total : ${
          response?.nb_rappels_paiement || 1
        } rappel(s)`,
      );
      await refreshAchats();
    } catch (error) {
      toast.error("Erreur lors de l'envoi du mail.");
    }
  };

  const toggleAutorisationAchat = async (reglement) => {
    try {
      await updateReglement(reglement.id, {
        ...reglement,
        autorisation_achat: !reglement.autorisation_achat,
      });
      toast.success("Autorisation mise à jour !");
      refreshAchats();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de l'autorisation.");
      console.error(error);
    }
  };

  // Message viewer
  const openMessage = (r) => {
    setMessageReglement(r);
    setMessageOpen(true);
  };
  const closeMessage = () => {
    setMessageOpen(false);
    setMessageReglement(null);
  };

  return (
    <section className="w-full">
      <ScrollIndicator />
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Gestion des Commandes & Paiements"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + boutons */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white space-y-3">
              <h3 className="text-lg sm:text-xl font-semibold text-center">
                Centralisez et suivez vos paiements d'articles boutique,
                règlements Licence-Cotisation ...
              </h3>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center">
                <span>Filtrez par saison, nom, article, statut et type.</span>{" "}
                <span>
                  Activez ou non l’affichage du paiement côté utilisateur.
                </span>{" "}
                <span>Envoyez des rappels en un clic.</span>
              </p>

              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  text="Ajouter un achat boutique"
                  onClick={() => setIsModalActive(true)}
                  icon={FaCartPlus}
                  className="!rounded-xl !bg-white/95 !text-slate-900 hover:!bg-white gap-2"
                />
                <Button
                  text="Ajouter une licence-cotisation-réduction"
                  onClick={() => setIsSpecialModalActive(true)}
                  icon={FaCartPlus}
                  className="!rounded-xl !bg-white/95 !text-slate-900 hover:!bg-white gap-2"
                />
                <Button
                  text={helpOpen ? "Masquer l'aide" : "Aide"}
                  onClick={() => setHelpOpen((v) => !v)}
                  icon={FaInfoCircle}
                  className="!rounded-xl !bg-white/10 !text-white hover:!bg-white/20 !ring-1 !ring-white/50 gap-2"
                  aria-expanded={helpOpen}
                />
              </div>
            </div>
          </div>

          {/* Aide */}
          {helpOpen && (
            <div className="px-5 py-5 sm:px-8 bg-sky-50/70 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800">
              <p className="text-slate-800 dark:text-slate-200 text-sm mb-2 text-center">
                <span className="font-semibold">
                  Bienvenue sur la page des Paiements des Achats
                </span>
              </p>
              <ul className="text-[13.5px] leading-6 text-slate-700 dark:text-slate-300 space-y-2 list-none">
                <li>
                  🔹 Sur le tableau apparaissent les achats effectués par les
                  utilisateurs en boutique, mais également les Règlements
                  Inscription que vous envoyez aux utilisateurs.
                </li>
                <li>
                  🔹 La saison active commence le 1er juillet et se termine le
                  30 juin de l'année suivante. Vous pouvez filtrer les achats
                  par saison, nom d'utilisateur, article, statut de règlement et
                  type de règlement.
                </li>
                <li>
                  🔹 Pour envoyer manuellement un règlement d’un article
                  physique (kimono, stages, passeports sportifs, textiles …),
                  utilisez le bouton{" "}
                  <span className="font-semibold">
                    Ajouter un achat boutique
                  </span>
                  .
                </li>
                <li>
                  🔹 Pour envoyer manuellement un{" "}
                  <span className="font-semibold">
                    Règlement de Licence-Assurance-Réduction
                  </span>
                  , utilisez le bouton{" "}
                  <span className="font-semibold">
                    Licence-Cotisation-Réduction
                  </span>{" "}
                  : sélectionnez l’utilisateur qui gère un ou plusieurs
                  adhérents, la saison, puis ajoutez les articles.
                </li>
                <li>
                  🔹 La colonne{" "}
                  <span className="font-semibold">Autorisation</span> permet
                  d’afficher/masquer le bouton de paiement côté utilisateur
                  (utile si vous préférez attendre la rentrée ou la validation
                  du cours d'essai).
                </li>
              </ul>
            </div>
          )}

          {/* Filtres */}
          <FiltersToolbar
            articles={articles}
            seasons={seasons}
            filterNom={filterNom}
            setFilterNom={setFilterNom}
            filterArticle={filterArticle}
            setFilterArticle={setFilterArticle}
            filterStatutReglementList={filterStatutReglementList}
            setFilterStatutReglementList={setFilterStatutReglementList}
            filterTypeReglement={filterTypeReglement}
            setFilterTypeReglement={setFilterTypeReglement}
            selectedSeason={selectedSeason}
            setSelectedSeason={setSelectedSeason}
          />

          {/* Totaux */}
          <TotalsBar
            totalAchats={totalAchats}
            totalAchatsCount={totalAchatsCount}
          />

          {/* TABLE DESKTOP */}
          <DesktopTable
            rows={filteredReglements}
            adherentsByUser={adherentsByUser}
            onToggleAutorisation={toggleAutorisationAchat}
            onSendReminder={handleSendReminderMail}
            onOpenReminderHistory={openReminderHistory}
            onEdit={openEditModal}
            onDelete={handleDeleteAchat}
            onOpenMessage={openMessage}
          />

          {/* CARTES MOBILE */}
          <MobileCards
            rows={filteredReglements}
            onOpenMessage={openMessage}
            onOpenReminderHistory={openReminderHistory}
          />
        </div>

        <ToastContainer />
      </div>

      {/* Modales CRUD */}
      <CreateAchatModal
        isActive={isModalActive}
        onClose={() => setIsModalActive(false)}
        refreshAchats={refreshAchats}
      />
      <CreateSpecialAchatModal
        isActive={isSpecialModalActive}
        onClose={() => setIsSpecialModalActive(false)}
        refreshAchats={refreshAchats}
      />
      <EditAchatModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        reglement={editableReglement}
        refreshAchats={refreshAchats}
      />

      {/* Modale Détails / Message */}
      <MessageModal
        isOpen={messageOpen}
        reglement={messageReglement}
        onClose={closeMessage}
        onReminder={handleSendReminderMail}
        onEdit={(r) => {
          closeMessage();
          openEditModal(r);
        }}
        onDelete={handleDeleteAchat}
      />

      {/* Modale Historique des rappels */}
      <ReminderHistoryModal
        isOpen={reminderHistoryOpen}
        reglement={reminderHistoryReglement}
        onClose={closeReminderHistory}
      />
    </section>
  );
};

export default Achats;
